import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Paper, Stack, Typography,
} from '@mui/material';
import {
  AutoAwesome, Block, Cancel, CheckCircle, Close, OpenInNew, Visibility,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { withSerialNo } from '../../components/ui/gridColumns';
import { getApiErrorMessage } from '../../services/userService';
import { KycRow, payoutApi } from '../../services/business/payoutService';
import {
  EMPTY_FILTERS, FilterState, PayoutFilterBar, PromptDialog, StatusChip, dateText, filterParams, personLabel, titleCase,
} from './payoutShared';
import { ActionIcon, ApprovalWarning, AutoCheckChip, AutoCheckPanel, suggestedReason } from './AutoCheckView';

const STATUS_TABS = ['pending', 'verified', 'rejected', ''];

function KycDocument({ kycId, index, kind, mimeType }: { kycId: string; index: number; kind: string; mimeType: string }) {
  const [url, setUrl] = useState<string>('');
  const [err, setErr] = useState('');
  useEffect(() => {
    let revoked = '';
    payoutApi.kycDocument(kycId, index)
      .then((blob) => { revoked = URL.createObjectURL(blob); setUrl(revoked); })
      .catch((e) => setErr(getApiErrorMessage(e, 'Could not load document')));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [kycId, index]);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" alignItems="center" mb={1}>
        <Typography variant="subtitle2" flex={1}>{titleCase(kind)}</Typography>
        {url && <IconButton size="small" href={url} target="_blank" rel="noreferrer"><OpenInNew fontSize="small" /></IconButton>}
      </Stack>
      {err && <Alert severity="error">{err}</Alert>}
      {!err && !url && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
      {url && mimeType.startsWith('image/') && (
        <Box component="img" src={url} alt={kind} sx={{ width: '100%', maxHeight: 280, objectFit: 'contain', bgcolor: 'grey.100', borderRadius: 1 }} />
      )}
      {url && !mimeType.startsWith('image/') && <Button href={url} target="_blank" rel="noreferrer">Open PDF</Button>}
    </Paper>
  );
}

type Decision = { id: string; row: KycRow; decision: 'verified' | 'rejected' };

export default function PayoutKycPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pending, setPending] = useState<Decision | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { search, userType } = filterParams(filters);
  const params = { status: status || undefined, search, userType, page: page + 1, limit: pageSize };
  const { data, isLoading, error } = useQuery({
    queryKey: ['payout-kyc', params],
    queryFn: () => payoutApi.kycList(params),
    refetchInterval: (q) => (q.state.data?.rows.some((r) => r.autoCheck?.state === 'running') ? 4000 : false),
  });
  const { data: detail, error: detailError } = useQuery({
    queryKey: ['payout-kyc-detail', selectedId],
    queryFn: () => payoutApi.kyc(selectedId!),
    enabled: !!selectedId,
    refetchInterval: (q) => (q.state.data?.autoCheck?.state === 'running' ? 4000 : false),
  });

  const review = useMutation({
    mutationFn: ({ id, decision, reason }: { id: string; decision: 'verified' | 'rejected'; reason?: string }) => payoutApi.reviewKyc(id, decision, reason),
    onSuccess: (_r, v) => {
      setPending(null);
      setSelectedId(null);
      setFlash({ type: 'success', text: v.decision === 'verified' ? 'KYC approved' : 'KYC rejected' });
      queryClient.invalidateQueries({ queryKey: ['payout-kyc'] });
      queryClient.invalidateQueries({ queryKey: ['payout-summary'] });
    },
    onError: (e) => setFlash({ type: 'error', text: getApiErrorMessage(e) }),
  });

  const autoCheck = useMutation({
    mutationFn: (id: string) => { setChecking(id); return payoutApi.autoVerifyKyc(id); },
    onSuccess: (r) => setFlash({ type: r.recommendation === 'reject' ? 'error' : 'success', text: `Verification assistant: ${r.score}/100 — ${r.summary}` }),
    onError: (e) => setFlash({ type: 'error', text: getApiErrorMessage(e) }),
    onSettled: () => {
      setChecking(null);
      queryClient.invalidateQueries({ queryKey: ['payout-kyc'] });
      queryClient.invalidateQueries({ queryKey: ['payout-kyc-detail'] });
    },
  });

  const columns: GridColDef<KycRow>[] = useMemo(() => [
    { field: 'submittedAt', headerName: 'Submitted', width: 160, valueFormatter: (v) => dateText(v as string) },
    { field: 'user', headerName: 'Partner account', flex: 1, minWidth: 150, valueGetter: (_v, row) => personLabel(row.user) },
    { field: 'fullName', headerName: 'Legal name', flex: 1, minWidth: 150 },
    { field: 'userType', headerName: 'Type', width: 120, valueFormatter: (v) => titleCase(v as string) },
    { field: 'idType', headerName: 'ID', width: 170, valueGetter: (_v, row) => `${titleCase(row.idType)} ${row.idNumberMasked}` },
    { field: 'documents', headerName: 'Docs', width: 60, valueGetter: (_v, row) => row.documents.length },
    {
      field: 'autoCheck', headerName: 'Assistant', width: 190, sortable: false,
      renderCell: ({ row }) => <Stack height="100%" justifyContent="center"><AutoCheckChip check={row.autoCheck} busy={checking === row._id} /></Stack>,
    },
    { field: 'status', headerName: 'Status', width: 110, renderCell: ({ value }) => <StatusChip status={value as string} /> },
    {
      field: 'actions', headerName: 'Actions', width: 170, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" alignItems="center" height="100%">
          <ActionIcon title="View documents" color="primary" onClick={() => setSelectedId(row._id)}><Visibility fontSize="small" /></ActionIcon>
          {row.status !== 'verified' && (
            <ActionIcon title="Approve KYC" color="success" onClick={() => setPending({ id: row._id, row, decision: 'verified' })}><CheckCircle fontSize="small" /></ActionIcon>
          )}
          {row.status === 'pending' && (
            <ActionIcon title="Reject KYC" color="error" onClick={() => setPending({ id: row._id, row, decision: 'rejected' })}><Cancel fontSize="small" /></ActionIcon>
          )}
          {row.status === 'verified' && (
            <ActionIcon title="Revoke KYC" color="error" onClick={() => setPending({ id: row._id, row, decision: 'rejected' })}><Block fontSize="small" /></ActionIcon>
          )}
          <ActionIcon title="Run verification assistant" color="info" disabled={!!checking || row.autoCheck?.state === 'running'} onClick={() => autoCheck.mutate(row._id)}>
            <AutoAwesome fontSize="small" />
          </ActionIcon>
        </Stack>
      ),
    },
  ], [checking]); // eslint-disable-line react-hooks/exhaustive-deps

  const k = detail;
  const pendingCheck = pending ? (pending.id === k?._id ? k?.autoCheck : pending.row.autoCheck) : undefined;
  const revoking = pending?.decision === 'rejected' && pending.row.status === 'verified';

  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance"
        title="Partner KYC"
        subtitle="Verify partner identity before they can withdraw. Every view of an ID number or document is recorded in the payout log."
      />
      {flash && <Alert severity={flash.type} onClose={() => setFlash(null)} sx={{ mb: 2 }}>{flash.text}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{getApiErrorMessage(error)}</Alert>}

      <Stack direction="row" gap={1} mb={2} flexWrap="wrap">
        {STATUS_TABS.map((s) => (
          <Chip key={s || 'all'} label={s ? titleCase(s) : 'All'} clickable
            color={status === s ? 'primary' : 'default'} variant={status === s ? 'filled' : 'outlined'}
            onClick={() => { setStatus(s); setPage(0); }} />
        ))}
      </Stack>
      <PayoutFilterBar value={filters} onChange={(f) => { setFilters(f); setPage(0); }} />

      <DataGrid
        rows={data?.rows || []}
        columns={withSerialNo(columns)}
        loading={isLoading}
        getRowId={(r) => r._id}
        autoHeight
        paginationMode="server"
        rowCount={data?.meta?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
        pageSizeOptions={[25, 50, 100]}
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />

      <Dialog open={!!selectedId} onClose={() => setSelectedId(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box flex={1}>KYC — {k?.fullName || ''}</Box>
          {k && <StatusChip status={k.status} />}
          <IconButton onClick={() => setSelectedId(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailError && <Alert severity="error">{getApiErrorMessage(detailError)}</Alert>}
          {!k ? <Typography>Loading…</Typography> : (
            <Stack gap={2}>
              <AutoCheckPanel check={k.autoCheck} busy={checking === k._id} onRun={() => autoCheck.mutate(k._id)} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="overline">Identity</Typography>
                  <Typography>Legal name: <b>{k.fullName}</b></Typography>
                  <Typography>Date of birth: {k.dateOfBirth || '—'}</Typography>
                  <Typography>{titleCase(k.idType)}: <b>{k.idNumber || k.idNumberMasked}</b></Typography>
                  <Typography>Tax ID (SSN / TIN): {k.taxId || k.taxIdMasked || '—'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="overline">Partner account</Typography>
                  <Typography>{personLabel(k.user)} · {titleCase(k.userType)}</Typography>
                  <Typography color="text.secondary">{k.user?.phone} {k.user?.email}</Typography>
                  <Typography variant="overline" display="block" mt={1}>Address</Typography>
                  <Typography>
                    {[k.addressLine1, k.addressLine2, k.city, k.state, k.postalCode, k.country].filter(Boolean).join(', ') || '—'}
                  </Typography>
                </Grid>
              </Grid>
              {k.rejectionReason && <Alert severity="warning">Last rejection: {k.rejectionReason}</Alert>}
              <Grid container spacing={2}>
                {k.documents.map((doc) => (
                  <Grid item xs={12} sm={6} key={doc.index}>
                    <KycDocument kycId={k._id} index={doc.index} kind={doc.kind} mimeType={doc.mimeType} />
                  </Grid>
                ))}
              </Grid>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>History</Typography>
                {k.history.map((h, i) => (
                  <Typography key={i} variant="body2" color="text.secondary">
                    {dateText(h.at)} · {titleCase(h.status)}{h.note ? ` — ${h.note}` : ''}
                  </Typography>
                ))}
              </Box>
            </Stack>
          )}
        </DialogContent>
        {k && (
          <DialogActions sx={{ p: 2 }}>
            {k.status !== 'rejected' && (
              <Button color="error" startIcon={k.status === 'verified' ? <Block /> : <Cancel />}
                onClick={() => setPending({ id: k._id, row: k, decision: 'rejected' })} disabled={review.isPending}>
                {k.status === 'verified' ? 'Revoke' : 'Reject'}
              </Button>
            )}
            {k.status !== 'verified' && (
              <Button variant="contained" color="success" startIcon={<CheckCircle />}
                onClick={() => setPending({ id: k._id, row: k, decision: 'verified' })} disabled={review.isPending}>
                Approve KYC
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>

      <PromptDialog
        open={pending?.decision === 'verified'}
        title={`Approve KYC — ${pending?.row.fullName || ''}`}
        description={<ApprovalWarning check={pendingCheck} subject="KYC" />}
        confirmLabel="Approve"
        confirmColor="success"
        fields={[]}
        busy={review.isPending}
        onClose={() => setPending(null)}
        onSubmit={() => pending && review.mutate({ id: pending.id, decision: 'verified' })}
      />
      <PromptDialog
        open={pending?.decision === 'rejected'}
        title={revoking ? 'Revoke KYC' : 'Reject KYC'}
        confirmLabel={revoking ? 'Revoke' : 'Reject'}
        confirmColor="error"
        fields={[{
          name: 'reason', label: 'Reason shown to the partner', required: true, minLength: 3, type: 'multiline',
          defaultValue: suggestedReason(pendingCheck),
        }]}
        busy={review.isPending}
        onClose={() => setPending(null)}
        onSubmit={(v) => pending && review.mutate({ id: pending.id, decision: 'rejected', reason: v.reason })}
      />
    </PageShell>
  );
}
